import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import {
  FormControl,
  FormBuilder,
  FormArray,
  ReactiveFormsModule,
  FormGroup,
  Validators,
} from '@angular/forms';

import { LoadConfigService } from '../../services/load-config.service';
import { Question, QuestionType } from '../../shared/question';
import { ArchiveDNSResultsService } from '../../services/archive-dns-results.service';
import { minSelected } from '../../shared/minSelected.validator';
import { maxSelected } from '../../shared/maxSelected.validator';
import { answerLength } from '../../shared/answerLength.validator';
import { inputTooHigh } from '../../shared/numberInputHigh.validator';
import { inputTooLow } from '../../shared/numberInputLow.validator';
import { StudyNavigationService } from '../../services/study-navigation.service';
import { Subscription } from 'rxjs';
import { InstructionService } from '../../services/instruction.service';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.scss'],
})
export class QuestionnaireComponent implements OnInit, OnDestroy {
  studyPosition: number = 0;
  private sub: Subscription;

  allQuestions: QuestionType[];

  rawQuestions: QuestionType[];
  rawQuestionnaire: FormGroup;

  // TODO: make sure that these are no longer necessary
  questions: Question[];
  questionnaire: FormGroup;

  submitButtonLabel: string;
  placeholderText: string;
  placeholderSelectAnswer: string;

  constructor(
    private loadConfigService: LoadConfigService,
    private fb: FormBuilder,
    private archiveResults: ArchiveDNSResultsService,
    private navigationService: StudyNavigationService,
    private instructionService: InstructionService
  ) {}

  ngOnInit(): void {
    this.init();
    this.sub = this.navigationService.navigationSubject.subscribe((info) => {
      this.studyPosition = info.data?.questionnairePosition;
      if (
        this.studyPosition >=
        this.loadConfigService.getQuestionnaireAmounts('EN')
      ) {
        return;
      }

      this.init();
    });

    this.submitButtonLabel = this.instructionService.getTranslationsForTerm(
      'QUESTIONNAIRE_SUBMIT_BUTTON_LABEL'
    );
    this.placeholderText = this.instructionService.getTranslationsForTerm(
      'QUESTIONNAIRE_PLACEHOLDER_TEXT'
    );
    this.placeholderSelectAnswer = this.instructionService.getTranslationsForTerm(
      'QUESTIONNAIRE_SELECT_PLACEHOLDER'
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private init() {
    this.rawQuestions = this.loadConfigService.getRawQuestions(
      this.instructionService.getLanguageIdentifier(),
      this.studyPosition
    );
    this.allQuestions = this.loadConfigService.getAllQuestions(
      this.instructionService.getLanguageIdentifier()
    );
    this.initQuestionnaire();
  }

  private getValidatorsRaw(question: QuestionType) {
    const validators = [];

    if (!question.required) {
      return validators;
    }

    if (question.required) {
      validators.push(Validators.required);
    }

    if (question.type === 'checkbox') {
      validators.push(minSelected(question.minNumberSelected));
      validators.push(maxSelected(question.maxNumberSelected));
    }

    if (question.type === 'drag&drop') {
      validators.push(answerLength(question.answers.length));
    }

    if (question.type === 'drag&drop_selection') {
      if (question.hasOwnProperty('minNumberSelected')) {
        validators.push(minSelected(question.minNumberSelected));
      } else {
        validators.push(minSelected(1));
      }

      if (question.hasOwnProperty('maxNumberSelected')) {
        validators.push(maxSelected(question.maxNumberSelected));
      }
    }

    if (question.type === 'number') {
      if (question.hasOwnProperty('maxRange')) {
        validators.push(inputTooHigh(question.maxRange));
      }

      if (question.hasOwnProperty('minRange')) {
        validators.push(inputTooLow(question.minRange));
      }
    }

    return validators;
  }

  private getNumberOfQuestionsBeforeCurrentQuestionnaire() {
    const questionsBeforeCurrentQuestionnaire = this.allQuestions.slice(
      0,
      this.studyPosition
    );
    if (questionsBeforeCurrentQuestionnaire.length === 0) {
      return 0;
    }
    const mergedQuestions = questionsBeforeCurrentQuestionnaire.reduce(
      (acc, value) => acc.concat(value),
      []
    );
    return mergedQuestions.length;
  }

  // don't need a way to keep track of the parent that triggered display of a question since it can be matched in the backend / post-analysis
  private initQuestionnaire() {
    const numberOfQuestionsBefore = this.getNumberOfQuestionsBeforeCurrentQuestionnaire();

    this.rawQuestionnaire = this.fb.group({});

    for (let i = 0; i < this.rawQuestions.length; i++) {
      const question: QuestionType = this.rawQuestions[i];
      const validators = this.getValidatorsRaw(question);
      const questionNr = numberOfQuestionsBefore + 1 + i;

      const questionWithoutSpaces = this.replaceSpace(
        `${question.questionID}_${question.question}`
      );

      // questionID was added in order to filter the question array for the correct question when archiving
      if (
        ['radio', 'checkbox', 'drag&drop', 'drag&drop_selection'].includes(
          question.type
        )
      ) {
        this.rawQuestionnaire.addControl(
          `${questionWithoutSpaces}_Group`,
          this.fb.group({
            [`${questionWithoutSpaces}_Array`]: this.fb.array([], validators),
            [questionWithoutSpaces]: [null],
            questionID: [question.questionID],
            questionNr: [questionNr],
            isVisible: [true],
          })
        );
      } else {
        this.rawQuestionnaire.addControl(
          `${questionWithoutSpaces}_Group`,
          this.fb.group({
            [questionWithoutSpaces]: [null, validators],
            questionID: [question.questionID],
            questionNr: [questionNr],
            isVisible: [true],
          })
        );
      }
    }
  }
  /**
   * Determines whether a specific question is visible, based on all conditions determined in client_config.json
   * @param questionWithoutSpaces Trimmed question as string
   * @returns Boolean
   */

  getQuestionVisibility(questionWithoutSpaces: string) {
    const questionID = (this.rawQuestionnaire.controls[
      `${questionWithoutSpaces}_Group`
    ] as FormGroup).controls['questionID'].value;

    const question = this.extractQuestionByID(questionID);

    const parentQuestions = question.parentQuestions;

    const hasEmptyParentQuestions =
      parentQuestions === undefined ||
      (Object.keys(parentQuestions).length === 0 &&
        parentQuestions.constructor === Object);

    if (hasEmptyParentQuestions) {
      return true;
    }

    const visibilityConditions = parentQuestions.conditions;

        // All conditions are parsed to check if they are met
    const isConditionFulfilled = visibilityConditions.some((conditionArray) => {
      return conditionArray
        .map((condition) =>
          this.checkIfAnswerWasGiven(condition.questionID, condition.condition)
        )
        .every((condition) => condition === true);
    });

    // this.rawQuestionnaire
    //   .get(`${questionWithoutSpaces}_Group`)
    //   .get('isVisible')
    //   .setValue(isConditionFulfilled);

    (this.rawQuestionnaire.controls[
      `${questionWithoutSpaces}_Group`
    ] as FormGroup).controls['isVisible'].setValue(isConditionFulfilled);

    if(!isConditionFulfilled) {
      (this.rawQuestionnaire.controls[
        `${questionWithoutSpaces}_Group`
      ] as FormGroup).disable()
    } else {
      (this.rawQuestionnaire.controls[
        `${questionWithoutSpaces}_Group`
      ] as FormGroup).enable()
    }



    return isConditionFulfilled;
  }
  
    /**
   * Checks whether the conditions are met for a specific questionID
   * @param questionID Question to be checked
   * @param answer Conditional answer
   * @returns Boolean
   */

  private checkIfAnswerWasGiven(questionID: string, answer: string | number) {
    for (const control in this.rawQuestionnaire.controls) {
      // const formGroup = this.rawQuestionnaire.get(control) as FormGroup;
      // const hasMatchingID = formGroup.get('questionID').value === questionID;

      const formGroup = this.rawQuestionnaire.controls[control] as FormGroup;
      const hasMatchingID =
        formGroup.controls['questionID'].value === questionID;

      if (!hasMatchingID) continue;

      // could probably change this to not be hardcoded and instead the the index of the last '_'
      const questionWithoutSpaces = control.slice(0, -6);
      const formArrayName = questionWithoutSpaces + '_Array';

      if (formGroup.contains(formArrayName)) {
        const formArray = formGroup.controls[formArrayName] as FormArray;

        for (let i = 0; i < formArray.length; i++) {
          if (formArray.at(i).value === answer) {
            return true;
          }
        }
      } else {
        const givenAnswer = formGroup.controls[questionWithoutSpaces].value;
        if (givenAnswer === answer) {
          return true;
        }
      }
    }

    return false;
  }

  private extractQuestionByID(questionID: string) {
    return this.rawQuestions.find(
      (question) => question.questionID === questionID
    );
  }

  getType(type: string): string {
    // have to make sure that type is of value 'text' | 'number' | 'dropdown' | 'radio' | 'select' | 'drag&drop'
    switch (type) {
      case 'text': {
        return 'textarea';
      }
      case 'dropdown': {
        return 'select';
      }
      default: {
        return type;
      }
    }
  }

  getAnswers(question: Question): string[] | number[] {
    if (question.Type === 'textarea' || question.Type === 'number') {
      return;
    }

    return question.answers;
  }

  replaceSpace(str: string): string {
    return str
      .trim()
      .replace(/\s/gi, '_')
      .replace(/[^a-zA-Z0-9 ]/g, "")
  }

  onSubmit(event) {
    event.preventDefault();
    this.archiveResults.saveQuestionnaireAnswers(this.rawQuestionnaire);
    this.navigationService.renderNext();
  }

  // https://www.positronx.io/angular-7-select-dropdown-examples-with-reactive-forms/
  onSelectChange(questionTitle: string, e): void {}

  // this method is used to update the answer array for questions using radio buttons or checkboxes
  onMultiChange(questionTitle: string, answer: string | number, e): void {
    const formArrayName = `${questionTitle}_Array`
    
    const formGroup = this.rawQuestionnaire.controls[
      `${questionTitle}_Group`
    ] as FormGroup
    const answerArray: FormArray = formGroup.controls[formArrayName] as FormArray;

    if (e.target.type === 'radio') {
      answerArray.clear();
    }

    if (e.target.checked) {
      answerArray.push(new FormControl(answer));
    } else {
      // used to increment through the elements of the array
      let i: number = 0;
      // currently does not take care of duplicate entries
      answerArray.controls.forEach((item: FormControl) => {
        if (item.value === answer) {
          answerArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  onSliderChange(questionTitle: string, e) {
    // const answer = this.rawQuestionnaire
    //   .get(questionTitle + '_Group')
    //   .get(questionTitle);
    // answer.setValue(e.value);
  }

  onRankingChange(questionTitle: string, e): void {
    let answerArray: FormArray = (this.rawQuestionnaire.controls[
      `${questionTitle}_Group`
    ] as FormGroup).controls[`${questionTitle}_Array`] as FormArray;

    answerArray.clear();
    e.answers.forEach((answer) => {
      answerArray.push(new FormControl(answer));
    });
  }
}
